// @ts-strict-ignore
/** @jsx jsx */
import { jsx, Styled } from 'theme-ui'
import connect from '@h/connect-redux'
import { sources } from '@r/elasticsearch/elasticsearch-selectors'
import { getDefaultCalculatorInputs } from '@r/calculators/calculators-selectors'
import querystring from 'querystring'
import { query } from '../../redux/app-selectors'
import ButtonWrapper from './button-wrapper'
import getWindow from '@u/get-window'

/** Generates the URL parameters to send to mojo based on data coming from get parameters
 * and data from the elastic search result */
export const getMojoURLParams = (queryParams, linkInfo = {}) => {
  let borrowerType = null
  let mortgageType = null

  // TODO: Replace this with an id check from a new field
  // eslint-disable-next-line camelcase
  if (linkInfo?.definition?.borrower_type) {
    borrowerType = linkInfo.definition.borrower_type
  }
  // eslint-disable-next-line camelcase
  if (linkInfo?.definition?.mortgage_type) {
    mortgageType = linkInfo.definition.mortgage_type
  }

  if (
    (linkInfo?.definition?.group === 'uswitchMojoHousePrices' &&
      linkInfo?.definition?.type === 'primary') ||
    linkInfo?.definition?.group === 'uswitchMojoV2HousePrices'
  ) {
    mortgageType = 'remortgage'
  }

  const mojoParams = {
    mortgageType,
    borrowerType,
    'basicPropertyDetails.propertyValue': queryParams.propertyValue,
    'borrowingDetails.mortgageBalance': queryParams.mortgageAmount,
    term: queryParams.repaymentPeriod,
  }
  // Just in case it being undefined breaks things, make sure it's properly deleted
  if (!borrowerType) {
    delete mojoParams.borrowerType
  }
  if (!mortgageType) {
    delete mojoParams.mortgageType
  }
  // Mortgage info gets encoded to base64 for mojo
  const jsonParams = JSON.stringify(mojoParams)
  const mojoEncoded = getWindow().btoa
    ? getWindow().btoa(jsonParams)
    : Buffer.from(jsonParams).toString('base64')

  return {
    seedData: mojoEncoded,
  }
}

/** The flow of this file explained (because it's getting a bit convoluted):
 * 1) the connect function parses query params
 * 2) The actual component finds the primary and secondary links from elastic search
 * 3) Each click out link gets mapped, and has its URL generated
 *   3) a) Depending on the brand url transformation, a different function is called to generate URL parameters to send to the brand
 *   3) b) the URL parameter generation uses a mix of get query parameters + content of the ES link to generate the final params to send out
 * 4) The links are created and their href is set to the ES link urls with the appropriate added parameters
 */
/** Maps transformation names to the functions to use to parse GET parameters
 * and send URL parameters to providers
 */
const urlTransformations = {
  mojo: {
    queryMap: getZooplaCalculatorParameters,
    providerMap: getMojoURLParams,
  },
  trussle: {
    queryMap: getZooplaCalculatorParameters,
    providerMap: getTrussleURLParams,
  },
}

/** Given the elastic search results:
 * Generate the URL params to send to the provider based on query params and ES data + contentful transformation
 * Generates the final object that the react component will use to create the actual button
 */
function mapESResults(results, queryParams, transformation) {
  return results.map((info, index) => {
    const urlParams = generateURLParams(queryParams, info, transformation)
    return {
      // eslint-disable-next-line camelcase
      ctaButtonText: info?.definition?.cta_button_text || '',
      id: info?.id || index,
      url: generateClickOutURL(info?.links, urlParams),
      // eslint-disable-next-line camelcase
      iconSrc: info?.definition?.icon_source || '',
      // eslint-disable-next-line camelcase
      iconAlt: info?.definition?.icon_alt || '',
      // eslint-disable-next-line camelcase
      label: info?.definition?.label || '',
    }
  })
}

const ClickOutLinks = (props) => {
  const {
    sources = [],
    queryParams = {},
    transformation,
    variant = 'icon.button',
    afterIcon,
    schema = {},
  } = props

  const { buttonVariant = '', wrapperVariant = 'icon' } = schema

  const primaryLinks = sources.filter(
    (source) => source.definition.type.toLowerCase() === 'primary',
  )
  const secondaryLinks = sources.filter((source) => source.definition.type === 'secondary')

  // Parse the primary link in advance to generate the final URL by combining elastic search info and url parameters.
  const parsedPrimaryLinks = mapESResults(primaryLinks, queryParams, transformation)
  const parsedSecondaryLinks = mapESResults(secondaryLinks, queryParams, transformation)

  return (
    <div>
      <Styled.div sx={{ variant: `elements.buttons.variants.${wrapperVariant}` }}>
        {parsedPrimaryLinks.map(({ ctaButtonText, iconSrc, iconAlt, id, url }) => (
          <ButtonWrapper
            buttonVariant={buttonVariant}
            variant={variant}
            sx={{ mr: [0, 'sm'], flex: '1 1 0px' }}
            href={url}
            key={id}
            afterIcon={afterIcon}
          >
            <Choose>
              <When condition={iconSrc && !afterIcon}>
                <span sx={{ variant: 'elements.buttons.variants.icon.span' }}>
                  <img
                    sx={{ variant: 'elements.buttons.variants.icon.img' }}
                    alt={iconAlt}
                    src={iconSrc}
                  />
                  {ctaButtonText}
                </span>
              </When>
              <Otherwise>{ctaButtonText}</Otherwise>
            </Choose>
          </ButtonWrapper>
        ))}
      </Styled.div>

      {parsedSecondaryLinks.map(({ ctaButtonText, label = 'this is a label', id, url }) => (
        <div sx={{ mb: ['sm', 'md'], mt: ['0', 'sm'] }} key={id}>
          {label && <p sx={{ my: 0, variant: 'elements.buttons.variants.icon.label' }}>{label}</p>}
          <p sx={{ my: 0 }}>
            <a sx={{ variant: 'styles.a' }} href={url}>
              {ctaButtonText}
            </a>
          </p>
        </div>
      ))}
    </div>
  )
}

export default connect((state, { query, transformation, filterFunction = () => true }) => {
  // This component parses query strings to pass data through to the click out
  const urlParams = parseQueryString(state, transformation)
  const inputs = getDefaultCalculatorInputs(state, query.sys.id)

  // Take the url parameters and override them with calculator inputs to get the most up to date values
  const queryParams = { ...urlParams, ...inputs }
  return {
    sources: sources(state, query.sys.id).filter(filterFunction),
    transformation,
    queryParams,
  }
})(ClickOutLinks)

/** If a transformation is needed, parses query parameters to use later in url transformations to send to brands */
function parseQueryString(state, transformation) {
  let queryParams = {}
  if (transformation !== undefined && typeof urlTransformations[transformation] === 'object') {
    const params = query(state)
    // Parse the query parameter into an object the provider map will use
    queryParams = urlTransformations[transformation].queryMap(params)
  }
  return queryParams
}
/**
 * generates URL parameters for the click out link based on query params.
 * @param {State} state redux state
 * @param {TransformationName} transformation the transformation name obtained from contentful data
 */
function generateURLParams(queryParams, linkInfo, transformation) {
  let urlParams = {}
  if (transformation !== undefined && typeof urlTransformations[transformation] === 'object') {
    // Get the final URL parameters for the provider based on the parameters
    urlParams = urlTransformations[transformation].providerMap(queryParams, linkInfo)
  }
  return urlParams
}

/**
 * Generates the URL for a click out given elastic search result and url parameters
 * @param {Elastic Search Result} linkInfo The elastic search result object
 * @param {Object} urlParams map of url parameters to add to the URL
 */
function generateClickOutURL(linkInfo, urlParams) {
  if (!linkInfo) {
    return '/'
  }
  /**
   * Not sure how we're supposed to know the name of the brand and link so the code is just taking the first link available
   * The structure of the elastic search link object is something like that:
   *  { bankrate: {
   *      'mojo zoopla cta': {
   *        destination_url: 'https://.......',
   *        ...}}
   */

  if (!Object.values(linkInfo)[0] || !Object.values(linkInfo)[0].default) {
    return ''
  }

  const linkObject = Object.values(linkInfo)[0].default

  /** TODO: Add the actual elastic search code once we know what the value looks like */
  const hrefLink = `${linkObject.destination_url}&${querystring.encode(urlParams)}`
  return hrefLink
}

/** ===============================
 * Provider transformation functions
 * ================================ */

/** Creates an object of parameters by reading GET params for the zoopla mortgage calculator */
function getZooplaCalculatorParameters(params) {
  // TODO: Replace param names here with what we actually get from zoopla
  const mortgageInfo = {
    propertyValue: parseInt(params.propertyvalue, 10) || null,
    mortgageAmount: parseInt(params.mortgageAmount, 10) || null,
    repaymentPeriod: parseInt(params.repaymentterm, 10) || null,
  }
  return mortgageInfo
}

/** Not sure what the trussle format will be, for now just using the basic object */
function getTrussleURLParams(queryParams) {
  return queryParams
}
